import AddCircleIcon from '@mui/icons-material/AddCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { Box, Button, Card, Grid, Tooltip } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Damage, ExtendedAccident } from '../../../../../backendsdk';
import { TrackedIconButton as IconButton } from '../../../../../components/TrackedComponents';
import useIsMobile from '../../../../../hooks/isMobile';
import AccidentDamageForm from '../../AccidentDamageDetails';
import AccidentWitnessForm from '../../AccidentWitnessDetails';

interface AccidentModalProps {
    details: ExtendedAccident;
    allowUpdate: boolean;
    setUpdateCallbacks: CallableFunction;
    setValidateCallbacks: CallableFunction;
    sending: boolean;
    setDetailsChanged: CallableFunction;
}

const ExtraDetailsTab: React.FC<AccidentModalProps> = (props: AccidentModalProps) => {
    const { t } = useTranslation();
    const { details } = props;
    const [newThirdParty, setNewThirdParty] = useState<boolean>(false);
    const isMobile = useIsMobile();

    const conditionalTooltip = (component: React.ReactNode) => {
        if (details.accident_id === 0) {
            return (
                <Tooltip title={t('content.accidents.form.please_save')} followCursor>
                    <Box>{component}</Box>
                </Tooltip>
            );
        } else {
            return component;
        }
    };

    return (
        <Grid container spacing={1} columns={2} sx={{ p: 1 }}>
            <Grid item xs={2} sm={1}>
                {details.damage_list.map((damage: Damage, idx: number) => {
                    return (
                        <Card sx={{ p: 1, mt: idx > 0 ? 1 : 0 }} key={damage.damage_id}>
                            {conditionalTooltip(
                                <AccidentDamageForm
                                    index={idx}
                                    key={idx}
                                    accident_id={details.accident_id}
                                    setNewThirdParty={setNewThirdParty}
                                    details={damage}
                                    setUpdateCallbacks={props.setUpdateCallbacks}
                                    setValidateCallbacks={props.setValidateCallbacks}
                                    allowUpdate={props.allowUpdate}
                                    disabled={props.sending}
                                    setDetailsChanged={props.setDetailsChanged}
                                >
                                    {idx === details.damage_list.length - 1 && !newThirdParty && props.allowUpdate ? (
                                        isMobile ? (
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                onClick={() => setNewThirdParty(true)}
                                                sx={{ mt: 1.5 }}
                                            >
                                                {t('content.accidents.damage.add')}
                                            </Button>
                                        ) : (
                                            <Tooltip title={t('content.accidents.damage.add')}>
                                                <Box>
                                                    <IconButton
                                                        id="btn-add-damage"
                                                        size="small"
                                                        onClick={() => setNewThirdParty(true)}
                                                        sx={{ position: 'absolute', bottom: 0, right: 0 }}
                                                    >
                                                        <AddCircleIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Tooltip>
                                        )
                                    ) : null}
                                </AccidentDamageForm>,
                            )}
                        </Card>
                    );
                })}
                {newThirdParty || details.damage_list.length === 0 ? (
                    <Card sx={{ p: 1, mt: 1 }}>
                        {conditionalTooltip(
                            <AccidentDamageForm
                                index={details.damage_list.length}
                                accident_id={details.accident_id}
                                setNewThirdParty={setNewThirdParty}
                                setUpdateCallbacks={props.setUpdateCallbacks}
                                setValidateCallbacks={props.setValidateCallbacks}
                                allowUpdate={props.allowUpdate}
                                disabled={props.sending}
                                setDetailsChanged={props.setDetailsChanged}
                            >
                                {details.damage_list.length !== 0 ? (
                                    isMobile ? (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => setNewThirdParty(false)}
                                            sx={{ mt: 1.5 }}
                                        >
                                            {t('content.accidents.damage.cancel')}
                                        </Button>
                                    ) : (
                                        <IconButton
                                            id="btn-cancel-add-damage"
                                            size="small"
                                            sx={{ position: 'absolute', bottom: 0, right: 0 }}
                                        >
                                            <CancelIcon fontSize="small" onClick={() => setNewThirdParty(false)} />
                                        </IconButton>
                                    )
                                ) : null}
                            </AccidentDamageForm>,
                        )}
                    </Card>
                ) : null}
            </Grid>
            <Grid item xs={2} sm={1}>
                <Card sx={{ height: '100%', p: 1 }}>
                    {conditionalTooltip(
                        <AccidentWitnessForm
                            details={details.witness_list[0]}
                            accidentDetails={details}
                            setUpdateCallbacks={props.setUpdateCallbacks}
                            allowUpdate={props.allowUpdate}
                            disabled={props.sending}
                        />,
                    )}
                </Card>
            </Grid>
        </Grid>
    );
};

export default ExtraDetailsTab;
